import { ImagePlus, Loader2, X } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
  MAX_ATTACHMENTS_PER_REQUEST,
  MAX_ATTACHMENT_BYTES,
  REPAIR_URGENCIES,
  REPAIR_URGENCY_LABELS,
  type RepairUrgency,
} from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { useLightbox } from '@/components/lightbox';
import { Hint } from '@/components/cargofl-ui';
import {
  attachmentUrl,
  useAttachToRequest,
  useRaiseRepairRequest,
} from '@/lib/repair-requests-api';
import type { MyAssignment } from '@/lib/operations-api';
import { cn } from '@/lib/utils';

/**
 * Report a problem with something issued to you.
 *
 * Two steps, and they are two steps for a real reason: a photo has to attach to a
 * request that already exists, so the description is submitted first and the files go
 * up against the returned id. The dialog hides that - it looks like one action - but
 * it means a failed upload does not lose the report the person just typed, which is
 * the part that took them effort.
 */
export function ReportProblemDialog({
  assignment,
  onClose,
}: {
  assignment: MyAssignment | null;
  onClose: () => void;
}) {
  const raise = useRaiseRepairRequest();
  const attach = useAttachToRequest();

  const [description, setDescription] = React.useState('');
  const [urgency, setUrgency] = React.useState<RepairUrgency>('NORMAL');
  const [files, setFiles] = React.useState<File[]>([]);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (assignment) {
      setDescription('');
      setUrgency('NORMAL');
      setFiles([]);
    }
  }, [assignment]);

  const addFiles = (chosen: FileList | null) => {
    if (!chosen) return;
    const next: File[] = [];
    for (const file of Array.from(chosen)) {
      if (files.length + next.length >= MAX_ATTACHMENTS_PER_REQUEST) {
        toast.warning(`At most ${MAX_ATTACHMENTS_PER_REQUEST} files.`);
        break;
      }
      // Checked here as a courtesy so the person is not told after typing a
      // paragraph. The API checks the file's actual bytes, which is the real control.
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${file.name} is over ${MAX_ATTACHMENT_BYTES / 1024 / 1024} MB.`);
        continue;
      }
      next.push(file);
    }
    setFiles((prev) => [...prev, ...next]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!assignment) return;

    try {
      const created = await raise.mutateAsync({
        assetId: assignment.asset.id,
        description: description.trim(),
        urgency,
      });

      // Uploaded one at a time so a single rejected file does not take the others
      // with it - and the report itself is already safely saved by this point.
      const failed: string[] = [];
      for (const file of files) {
        try {
          await attach.mutateAsync({ id: created.id, file });
        } catch {
          failed.push(file.name);
        }
      }

      if (failed.length > 0) {
        toast.warning(
          `Reported ${created.assetCode}, but ${failed.join(', ')} could not be attached. You can add photos from the list.`,
        );
      } else {
        toast.success(`Reported. IT can see it against ${created.assetCode}.`);
      }
      onClose();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? (Object.values(error.fieldErrors ?? {})[0]?.[0] ?? error.message)
          : 'That could not be sent.';
      toast.error(message);
    }
  };

  const busy = raise.isPending || attach.isPending;

  return (
    <Dialog open={assignment !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>
            {assignment?.asset.name}{' '}
            <span className="at-num">({assignment?.asset.code})</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>What is wrong?</Label>
            <Textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="The screen flickers when the lid is moved. Started about a week ago and is getting worse."
              required
              minLength={10}
            />
            <p className="text-[11px] text-muted-foreground">
              What happens, and when it started. IT reads this to decide whether it goes to
              a vendor.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>How much is it affecting you?</Label>
            <div className="grid gap-1.5">
              {REPAIR_URGENCIES.map((level) => (
                <Hint label={'How urgent it is'}><button
                  key={level}
                  type="button"
                  onClick={() => setUrgency(level)}
                  className={cn(
                    'rounded-md border px-3 py-2 text-left text-sm transition-colors',
                    urgency === level ? 'border-primary bg-accent' : 'hover:bg-muted/50',
                  )}
                >
                  {REPAIR_URGENCY_LABELS[level]}
                </button></Hint>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Photos (optional)</Label>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              A photo of the damage usually settles in one second what a paragraph cannot.
              JPEG, PNG, WebP, HEIC or PDF, up to{' '}
              {MAX_ATTACHMENT_BYTES / 1024 / 1024} MB each.
            </p>

            {files.length > 0 && (
              <ul className="space-y-1.5">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-2 rounded-md border p-2 text-xs"
                  >
                    {/* A local preview, so they can see they picked the right photo
                        before it goes anywhere. */}
                    {file.type.startsWith('image/') && (
                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="size-9 shrink-0 rounded object-cover"
                        onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                      />
                    )}
                    <span className="min-w-0 flex-1 truncate">{file.name}</span>
                    <span className="at-num shrink-0 text-muted-foreground">
                      {Math.round(file.size / 1024)} KB
                    </span>
                    <Hint label={`Remove ${file.name}`}><button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="size-3.5" />
                    </button></Hint>
                  </li>
                ))}
              </ul>
            )}

            {files.length < MAX_ATTACHMENTS_PER_REQUEST && (
              <>
                <Button hint={"Attach a photo of the problem"}
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => inputRef.current?.click()}
                >
                  <ImagePlus className="mr-1.5 size-4" /> Add a photo
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  // A hint to the picker, not a control - the API verifies the bytes.
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </>
            )}
          </div>

          <Alert>
            <AlertDescription className="text-xs leading-relaxed">
              Reporting this does not take the asset out of service — keep using it. IT
              decides whether it goes for repair, and you will see what they decided here.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button hint={"Send the report to IT"} type="submit" disabled={busy || description.trim().length < 10}>
              {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              {busy ? 'Sending…' : 'Send report'}
            </Button>
            {description.trim().length < 10 && <span className="self-center text-xs text-muted-foreground">Describe the problem — at least 10 characters</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A small gallery of what was attached, used on both sides of the flow. */
export function AttachmentStrip({
  attachments,
}: {
  attachments: Array<{ id: string; filename: string; mimeType: string }>;
}) {
  /**
   * Images the browser turned out not to be able to decode.
   *
   * HEIC is transcoded to JPEG on upload now, but every iPhone photo attached BEFORE
   * that is still sitting in the table as image/heic, and Chrome cannot render one. A
   * broken icon reads to the reporter as "my photo never uploaded", so anything that
   * fails to load falls back to the same named link a PDF gets - which does open, since
   * the browser hands an unknown type to the OS.
   *
   * Keyed off the <img>'s own error event rather than off a HEIC allowlist: it then
   * also covers a truncated file or a decode the browser simply refuses, without
   * needing to predict which types those are.
   */
  const [undecodable, setUndecodable] = React.useState<ReadonlySet<string>>(new Set());

  const viewable = attachments.filter(
    (file) => file.mimeType.startsWith('image/') && !undecodable.has(file.id),
  );
  // Built from the SAME list the thumbnails render, so the arrow keys walk exactly what
  // is on screen - a photo that failed to decode is not silently in the slider as a
  // blank frame.
  const lightbox = useLightbox(
    viewable.map((file) => ({
      id: file.id,
      url: attachmentUrl(file.id),
      filename: file.filename,
    })),
  );

  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {attachments.map((file) =>
        viewable.some((v) => v.id === file.id) ? (
          <button
            key={file.id}
            type="button"
            title={file.filename}
            aria-label={`Open ${file.filename}`}
            onClick={() => lightbox.open(file.id)}
            className="rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <img
              src={attachmentUrl(file.id)}
              alt={file.filename}
              loading="lazy"
              onError={() => setUndecodable((previous) => new Set(previous).add(file.id))}
              className="size-16 rounded border object-cover transition-opacity hover:opacity-80"
            />
          </button>
        ) : (
          /* A PDF, or an image this browser cannot decode. Nothing to put in a slider,
             so it keeps opening in a tab where the OS handles it. */
          <a
            key={file.id}
            href={attachmentUrl(file.id)}
            target="_blank"
            rel="noreferrer"
            title={file.filename}
            className="flex h-16 max-w-40 items-center rounded border px-3 text-xs hover:bg-muted"
          >
            <span className="truncate">{file.filename}</span>
          </a>
        ),
      )}
      {lightbox.element}
    </div>
  );
}
